import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, Input, Spin, Checkbox } from 'antd';
import { PrimaryButton, H1, Colors, device } from 'utils';
import { PaymentActions } from 'redux/actions';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';

const FormItem = Form.Item;
const TextArea = Input.TextArea;

const InfoFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 16px 0;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: rgba(0, 0, 0, 0.85);
 
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    font-family: 'Inter', sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #414651;
  }

  .ant-form-item-label > label::before,
  .ant-form-item-label > label::after {
    display: none !important;
  }

  .ant-input,
  .ant-input-affix-wrapper {
    box-sizing: border-box;
    height: 40px;
    padding: 8px 12px;
    background: #ffffff;
    border: 1px solid #d5d7da;
    box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05);
    border-radius: 8px;
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: #181d27;
  }

  .ant-input::placeholder,
  textarea.ant-input::placeholder {
    color: #717680;
  }

  textarea.ant-input {
    min-height: 96px;
    resize: vertical;
    padding-top: 10px;
  }

  .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #414651;

    a {
      color: #3f621a;
      text-decoration: underline;
      margin: 0 3px;
    }
  }

  .ant-checkbox {
    top: 2px;
  }
`;

const PreviewCard = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 16px;
  gap: 4px;

  width: 100%;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid #ffffff;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.09);
  backdrop-filter: blur(2px);
  border-radius: 8px;
`;

const PreviewContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const PreviewAvatar = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 9999px;
  background: #fff4ed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;

const PreviewTexts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const PreviewTop = styled.div`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #3f621a;
`;

const PreviewMessage = styled.div`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #717680;
  word-break: break-word;
`;

const PreviewMeta = styled.div`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: #717680;
`;

const PreviewFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 8px;
  gap: 8px;
`;

const PreviewAction = styled.button`
  background: transparent;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;

  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #3f621a;

  &:hover {
    background: #f5fbee;
  }
`;

const PreviewHint = styled.p`
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: rgba(0, 0, 0, 0.85);
`;

export class InfoCardForm extends Component {
  static propTypes = {
    prepareCard: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    errors: PropTypes.array.isRequired,
    card: PropTypes.object.isRequired,
    setCurrentView: PropTypes.func.isRequired,
  };

  state = {
    previewMessage: 'Ich unterstütze dieses Projekt von Herzen 💚',
  };

  onFinish = (values) => {
    const { prepareCard, setCurrentView } = this.props;
    prepareCard(values);
    setCurrentView('card');
  };

  render() {
    const { loading } = this.props;
    const { previewMessage } = this.state;

    return (
      <InfoFormWrapper>
        <Spin spinning={loading}>
          <StyledForm
            ref={(z) => (this.form = z)}
            name="personal_info"
            onFinish={this.onFinish}
            initialValues={{
              terms: true,
              preview_message: previewMessage,
            }}
            layout="vertical"
          >
            <Section>
              <SectionTitle>Persönliche Angaben</SectionTitle>

              <FormItem
                label="Vorname"
                name="first_name"
                rules={[{ required: true }]}
              >
                <Input placeholder="Vorname eingeben" />
              </FormItem>

              <FormItem
                label="Nachname"
                name="last_name"
                rules={[{ required: true }]}
              >
                <Input placeholder="Nachname eingeben" />
              </FormItem>

              <FormItem
                label="E-Mail"
                name="email"
                rules={[{ required: true, type: 'email' }]}
              >
                <Input type="email" placeholder="name@email.de" />
              </FormItem>
            </Section>

            <Section>
              <SectionTitle>Vorschau</SectionTitle>

              <FormItem label="Personalisierung" name="preview_message">
                <TextArea
                  maxLength={150}
                  placeholder="Schreibe eine kurze Botschaft"
                  onChange={(e) =>
                    this.setState({ previewMessage: e.target.value })
                  }
                />
              </FormItem>

              <PreviewCard>
                <PreviewContent>
                  <PreviewAvatar>💚</PreviewAvatar>

                  <PreviewTexts>
                    <PreviewTop>
                      Deine Unterstützung wird so angezeigt
                    </PreviewTop>
                    <PreviewMessage>
                      {previewMessage?.trim()
                        ? previewMessage
                        : 'Ich unterstütze dieses Projekt von Herzen 💚'}
                    </PreviewMessage>
                    <PreviewMeta>Gerade eben</PreviewMeta>
                  </PreviewTexts>
                </PreviewContent>
              </PreviewCard>

              <PreviewFooter>
                <PreviewAction type="button">
                  Motiviere deine Geschwister, etwas zu dem Vorhaben
                  beizutragen!
                </PreviewAction>
                <PreviewHint>
                  Deine Botschaft erscheint in der Vorschau, bevor du zur
                  Zahlung weitergehst.
                </PreviewHint>
              </PreviewFooter>
            </Section>

            <Section>
              <FormItem
                name="terms"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error('Bitte akzeptiere die Bedingungen.')
                          ),
                  },
                ]}
              >
                <Checkbox>
                  Ich akzeptiere die
                  <Link to="/anb">Nutzungsbedingungen</Link>
                  und
                  <Link to="/datenschutz">Datenschutzbestimmungen</Link>
                  und bestätige, dass ich mindestens 18 Jahre alt bin.
                </Checkbox>
              </FormItem>

              <FormItem>
                <PrimaryButton type="primary" htmltype="submit" minWidth="100%">
                  Weiter zur Zahlung
                </PrimaryButton>
              </FormItem>
            </Section>
          </StyledForm>
        </Spin>
      </InfoFormWrapper>
    );
  }
}

const mapStateToProps = (state) => {
  const { card, loading, errors } = state.payment;
  return {
    card,
    loading,
    errors,
  };
};

const mapDispatchToProps = (dispatch) => ({
  prepareCard: (user) => dispatch(PaymentActions.prepareCard(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InfoCardForm);
